/**
 * ============================================================================
 * SECURITY ACTIVITY COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Display security events and activity log
 */

import React, { useState, useEffect } from 'react';
import { useSecurity } from '../../hooks/useSecurity';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import StatusBadge from '../common/StatusBadge';
import EmptyState from '../common/EmptyState';
import Tabs from '../common/Tabs';
import Select from '../common/Select';

/**
 * Security Activity Component
 * @returns {JSX.Element}
 */
const SecurityActivity = () => {
  const {
    securityEvents,
    loginHistory,
    loadSecurityEvents,
    loadLoginHistory,
    loading,
  } = useSecurity();
  const { showError } = useToast();
  const { formatDate, formatTime, language } = useI18n();

  const [activeTab, setActiveTab] = useState('events');
  const [eventFilter, setEventFilter] = useState('all');

  useEffect(() => {
    if (activeTab === 'events') {
      loadSecurityEvents({ type: eventFilter === 'all' ? undefined : eventFilter });
    } else {
      loadLoginHistory();
    }
  }, [activeTab, eventFilter, loadSecurityEvents, loadLoginHistory]);

  const getEventIcon = (type) => {
    const icons = {
      failed_login: '🔒',
      suspicious_ip: '🌐',
      rate_limit_violation: '⏱️',
      unauthorized_access: '⚠️',
      webhook_failure: '🔗',
      payment_anomaly: '💳',
      admin_action: '👤',
      token_compromise: '🎫',
      brute_force: '💥',
      sql_injection_attempt: '💉',
      xss_attempt: '📝',
      file_upload_abuse: '📎',
    };
    return icons[type] || '🔔';
  };

  const getEventColor = (type) => {
    const colors = {
      failed_login: 'from-red-500 to-pink-500',
      suspicious_ip: 'from-orange-500 to-red-500',
      rate_limit_violation: 'from-yellow-500 to-orange-500',
      unauthorized_access: 'from-red-500 to-pink-500',
      webhook_failure: 'from-blue-500 to-cyan-500',
      payment_anomaly: 'from-purple-500 to-pink-500',
      admin_action: 'from-green-500 to-emerald-500',
      token_compromise: 'from-red-500 to-pink-500',
      brute_force: 'from-red-500 to-pink-500',
      sql_injection_attempt: 'from-red-500 to-pink-500',
      xss_attempt: 'from-red-500 to-pink-500',
      file_upload_abuse: 'from-orange-500 to-red-500',
    };
    return colors[type] || 'from-gray-500 to-slate-500';
  };

  const getLoginStatusIcon = (success) => {
    return success ? '✅' : '❌';
  };

  const tabs = [
    {
      id: 'events',
      label: language === 'fr' ? 'Événements' : 'Events',
      count: securityEvents?.length || 0,
    },
    {
      id: 'logins',
      label: language === 'fr' ? 'Connexions' : 'Logins',
      count: loginHistory?.length || 0,
    },
  ];

  const eventTypes = [
    { value: 'all', label: language === 'fr' ? 'Tous' : 'All' },
    { value: 'failed_login', label: language === 'fr' ? 'Échecs de connexion' : 'Failed Logins' },
    { value: 'suspicious_ip', label: language === 'fr' ? 'IP suspecte' : 'Suspicious IP' },
    { value: 'unauthorized_access', label: language === 'fr' ? 'Accès non autorisé' : 'Unauthorized Access' },
  ];

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-black text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-2xl">📋</span>
            {language === 'fr' ? 'Activité de sécurité' : 'Security Activity'}
          </h3>
          {activeTab === 'events' && (
            <Select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              options={eventTypes}
              className="w-48"
            />
          )}
        </div>

        {/* Tabs */}
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} variant="pills" />

        {/* Content */}
        {loading && (!securityEvents && !loginHistory) ? (
          <div className="p-12 text-center">
            <Spinner size="lg" />
          </div>
        ) : activeTab === 'events' ? (
          securityEvents && securityEvents.length > 0 ? (
            <div className="space-y-3">
              {securityEvents.slice(0, 20).map((event, index) => (
                <Card
                  key={event.id || index}
                  variant="outlined"
                  className="bg-gray-50 dark:bg-gray-800/50 animate-fadeInUp"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getEventColor(
                        event.type
                      )} flex items-center justify-center text-xl shadow-lg flex-shrink-0`}
                    >
                      {getEventIcon(event.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-black text-gray-900 dark:text-white text-sm">
                          {event.type?.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </h4>
                        {event.severity && (
                          <StatusBadge variant={event.severity === 'high' ? 'error' : 'warning'} size="sm">
                            {event.severity}
                          </StatusBadge>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        {event.description || event.message}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                        {event.ipAddress && <span>IP: {event.ipAddress}</span>}
                        {event.timestamp && (
                          <span>
                            {formatDate(event.timestamp)} {formatTime(event.timestamp)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <EmptyState
              icon="📋"
              title={language === 'fr' ? 'Aucun événement' : 'No events'}
              description={
                language === 'fr'
                  ? 'Aucun événement de sécurité récent'
                  : 'No recent security events'
              }
            />
          )
        ) : loginHistory && loginHistory.length > 0 ? (
          <div className="space-y-3">
            {loginHistory.slice(0, 20).map((login, index) => (
              <Card
                key={login.id || index}
                variant="outlined"
                className={`${
                  login.success
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                } animate-fadeInUp`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="flex items-center gap-4">
                  <div className="text-2xl">{getLoginStatusIcon(login.success)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-black text-gray-900 dark:text-white text-sm">
                        {login.success
                          ? language === 'fr'
                            ? 'Connexion réussie'
                            : 'Successful Login'
                          : language === 'fr'
                          ? 'Échec de connexion'
                          : 'Failed Login'}
                      </h4>
                      <StatusBadge variant={login.success ? 'success' : 'error'} size="sm">
                        {login.success
                          ? language === 'fr'
                            ? 'Réussi'
                            : 'Success'
                          : language === 'fr'
                          ? 'Échec'
                          : 'Failed'}
                      </StatusBadge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      {login.ipAddress && <span>IP: {login.ipAddress}</span>}
                      {login.userAgent && (
                        <span className="truncate">{login.userAgent.split(' ')[0]}</span>
                      )}
                      {login.timestamp && (
                        <span>
                          {formatDate(login.timestamp)} {formatTime(login.timestamp)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState
            icon="🔐"
            title={language === 'fr' ? 'Aucune connexion' : 'No logins'}
            description={
              language === 'fr'
                ? 'Aucun historique de connexion'
                : 'No login history'
            }
          />
        )}
      </div>
    </Card>
  );
};

export default SecurityActivity;
